// __EQUIP_V2__ (2026-10-04) How long a driver's day already is, and how long the work
// being assigned takes - the inputs of timingIssues() in utils/equipmentCompat.js.
//
// Henry: "A driver is already on a 7 hr route and I'm trying to add another 8 hr route -
// warn me about timing constraints ... Warn about HOS rules ... but no hard block."
//
// His other work that day = his non-finished jobs booked for that day (loads.scheduled_date),
// timed from the route plan stamped on their stops (load_stops.planned_*: drive into each
// stop, time at it, start prep) when it exists, else the job's estimated_duration_hours, else
// 2 h per job ("rough estimate"). The previous day's last planned departure gives the
// time off before this day (the 8 h HOS rest).
import { supabase } from '../supabaseClient';

const FINISHED = '("complete","completed","ready_to_bill","billed","archived","cancelled")';
const ROUGH_JOB_MIN = 120;
const toDate = (s) => { const t = s ? Date.parse(/([zZ]|[+-]\d\d:?\d\d)$/.test(String(s)) ? String(s).replace(' ', 'T') : String(s).replace(' ', 'T') + 'Z') : NaN; return Number.isFinite(t) ? t : null; };
const prevDay = (day) => { const d = new Date(day + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() - 1); return d.toISOString().slice(0, 10); };

// One job's own time: planned stops, else its estimate, else 2 h.
export function jobTime(load) {
  const st = (load && load.load_stops) || [];
  const planned = st.filter((s) => s.planned_dwell_min != null || s.planned_leg_min != null);
  if (planned.length) {
    const drive = planned.reduce((a, s) => a + (Number(s.planned_leg_min) || 0), 0);
    const dwell = planned.reduce((a, s) => a + (Number(s.planned_dwell_min) || 0), 0);
    const prep = Math.max(0, ...planned.map((s) => Number(s.planned_prep_min) || 0));
    const starts = planned.map((s) => toDate(s.planned_arrival_at) !== null ? toDate(s.planned_arrival_at) - ((Number(s.planned_leg_min) || 0) + (Number(s.planned_prep_min) || 0)) * 60000 : null).filter((x) => x !== null);
    const ends = planned.map((s) => toDate(s.planned_departure_at)).filter((x) => x !== null);
    return { onDutyMin: drive + dwell + prep, driveMin: drive, start: starts.length ? Math.min(...starts) : null, end: ends.length ? Math.max(...ends) : null, source: 'route plan' };
  }
  const h = Number(load && load.estimated_duration_hours);
  if (h > 0) return { onDutyMin: h * 60, driveMin: 0, start: null, end: null, source: 'job estimate' };
  return { onDutyMin: ROUGH_JOB_MIN, driveMin: 0, start: null, end: null, source: 'rough' };
}

// Sum several jobs' times into one day.
export function sumDay(times) {
  const t = times || [];
  const starts = t.map((x) => x.start).filter((x) => x != null);
  const ends = t.map((x) => x.end).filter((x) => x != null);
  return {
    onDutyMin: t.reduce((a, x) => a + (x.onDutyMin || 0), 0),
    driveMin: t.reduce((a, x) => a + (x.driveMin || 0), 0),
    start: starts.length ? new Date(Math.min(...starts)).toISOString() : null,
    end: ends.length ? new Date(Math.max(...ends)).toISOString() : null,
    source: t.some((x) => x.source === 'rough') ? 'rough' : (t.length ? t[0].source : 'none'),
    jobs: t.length,
  };
}

// The driver's other work on `day` (jobs in excludeIds - the ones being assigned - left out).
export async function driverDay(driverId, day, excludeIds = []) {
  const empty = { existing: { onDutyMin: 0, driveMin: 0, start: null, end: null, source: 'none', jobs: 0 }, prevDayEnd: null };
  if (!driverId || !/^\d{4}-\d{2}-\d{2}$/.test(String(day || ''))) return empty;
  const prev = prevDay(day);
  const { data, error } = await supabase.from('loads')
    .select('id, scheduled_date, estimated_duration_hours, status, load_stops(planned_arrival_at, planned_departure_at, planned_leg_min, planned_prep_min, planned_dwell_min)')
    .eq('driver_id', driverId).in('scheduled_date', [day, prev]).not('status', 'in', FINISHED);
  if (error || !data) return empty;
  const skip = new Set(excludeIds || []);
  const today = data.filter((l) => l.scheduled_date === day && !skip.has(l.id)).map(jobTime);
  const ends = data.filter((l) => l.scheduled_date === prev)
    .flatMap((l) => (l.load_stops || []).map((s) => toDate(s.planned_departure_at))).filter((x) => x !== null);
  return { existing: sumDay(today), prevDayEnd: ends.length ? new Date(Math.max(...ends)).toISOString() : null };
}

// A dry-run route (calculateDriverDailyRoute / draftTimeline result) -> the work it adds.
export function timelineTime(tl) {
  if (!tl || !tl.success) return null;
  const stops = tl.stopsWithETAs || [];
  const drive = stops.reduce((a, s) => a + (Number(s.legSeconds) || 0), 0) / 60;
  const dwell = stops.reduce((a, s) => a + (Number(s.dwellMinutes) || 0), 0);
  const prep = Number(tl.prepMinutes) || 0;
  const first = stops[0];
  const start = first && toDate(first.estimatedArrival) !== null
    ? new Date(toDate(first.estimatedArrival) - ((Number(first.legSeconds) || 0) * 1000) - prep * 60000).toISOString() : null;
  return { onDutyMin: drive + dwell + prep, driveMin: drive, start, end: tl.estimatedEndTime || null, source: 'route timeline' };
}
